import { If } from '@/components/utils/conditionalComponents';
import {
    AllowedInputValue,
    AllowedInputValueItem,
    InputState,
    polymorphicIsEqual,
    polymorphicIsNotEmpty,
} from '@/utils/inputState';
import { Children, ReturnComponent } from '@/utils/reactTypes';
import { useUniqueId } from '@/utils/useUniqueId';
import {
    Box,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SxProps,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { FieldWithAxleLabel, labelWithRequiredAsterisk } from './AxleLabel';

export type AxleSelectFieldProps<Value extends AllowedInputValue<AllowedInputValueItem>, Option> = {
    label?: string;
    description?: Children;
    inlineLabel?: boolean;
    includeHelperText?: boolean;
    enabled?: boolean;
    inputState: InputState<Value>;
    options: Option[];
    optionToValue: (option: Option) => Value;
    optionToLabel: (option: Option) => string;
    renderOption?: (option: Option) => ReturnComponent;
    // Overrides how the closed field displays the current value. Useful when the selected value
    //  isn't (or is no longer) one of `options` — e.g. a since-deactivated choice that should still
    //  display but not be re-selectable from the list.
    renderValue?: (value: Value) => React.ReactNode;
    // Shown in the closed field while the value is empty.
    placeholder?: string;
    sx?: SxProps;
    _extraSelectStyles?: React.CSSProperties;
};

// If you have 3–4 fixed options, this is fine to use.
//  Otherwise, use <AxleAutocompleteField /> instead so the user can type to filter the options.
export const AxleSelectField = <Option, Value extends AllowedInputValue<AllowedInputValueItem>>({
    label = '',
    description = '',
    inlineLabel = false,
    includeHelperText = true,
    enabled = true,
    inputState: { value, required, reset, setInputState, showError, helperText, setIsTouched },
    options,
    optionToValue,
    optionToLabel,
    renderOption,
    renderValue,
    placeholder,
    _extraSelectStyles,
    sx,
}: AxleSelectFieldProps<Value, Option>) => {
    // If the options change, then mark the input as untouched, and reset the value.
    const [lastRecordedOptions, setLastRecordedOptions] = useState(options);
    useEffect(() => {
        // Comparing arrays or objects in Javascript doesn't work by itself.
        if (polymorphicIsEqual(options, lastRecordedOptions)) {
            return;
        }
        setLastRecordedOptions(options);

        setIsTouched(false);
        // If there’s only one option, just select that automatically.
        if (options.length === 1) {
            setInputState(optionToValue(options[0]));
        } else {
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    const inputLabelId = useUniqueId({ prefix: 'select-label-' });

    const hasPlaceholder = placeholder !== undefined;
    const renderValueOrPlaceholder = hasPlaceholder
        ? (selected: Value) => {
              if (!polymorphicIsNotEmpty(selected)) {
                  return (
                      // Matches the opacity MUI gives a native input's ::placeholder.
                      <Box
                          component='span'
                          sx={(theme) => ({ opacity: theme.palette.mode === 'light' ? 0.42 : 0.5 })}
                      >
                          {placeholder}
                      </Box>
                  );
              }
              if (renderValue) {
                  return renderValue(selected);
              }
              const selectedOption = options.find((option) =>
                  polymorphicIsEqual(optionToValue(option), selected),
              );
              if (selectedOption === undefined) {
                  return null;
              }
              return renderOption ? renderOption(selectedOption) : optionToLabel(selectedOption);
          }
        : renderValue;

    return (
        <FieldWithAxleLabel
            label={label}
            inlineLabel={inlineLabel}
            required={required}
            enabled={enabled}
            description={description}
            sx={sx}
        >
            <FormControl variant='outlined' fullWidth>
                <InputLabel
                    id={inputLabelId}
                    // An empty value reads as unfilled, so the label would cover the placeholder.
                    shrink={hasPlaceholder ? true : undefined}
                    sx={{
                        // Without this bit of CSS, the label isn't aligned properly.
                        '&[data-shrink="false"]': {
                            transform: 'translate(10px, 10px) scale(1)',
                        },
                    }}
                >
                    {inlineLabel ? labelWithRequiredAsterisk({ label, required }) : undefined}
                </InputLabel>
                <Select
                    labelId={inputLabelId}
                    label={inlineLabel ? labelWithRequiredAsterisk({ label, required }) : undefined}
                    disabled={!enabled}
                    // MUI warns about an out-of-range value for null but accepts '' as empty.
                    value={hasPlaceholder && value === null ? ('' as Value) : value}
                    displayEmpty={hasPlaceholder}
                    notched={hasPlaceholder ? true : undefined}
                    error={showError}
                    renderValue={renderValueOrPlaceholder}
                    onChange={(event) =>
                        setInputState(
                            // The typing only allows us to pass strings, but we want to pass
                            //  in an arbitrarily complex value; hence the type assertion.
                            event.target.value as Value,
                        )
                    }
                    style={{
                        height: '2.5em',
                        ..._extraSelectStyles,
                    }}
                >
                    {options.map((option, idx) => (
                        <MenuItem
                            key={idx}
                            value={
                                // The typing only allows us to pass strings, but we want to pass
                                //  in an arbitrarily complex value; hence the type assertion.
                                optionToValue(option) as string
                            }
                        >
                            {renderOption ? renderOption(option) : optionToLabel(option)}
                        </MenuItem>
                    ))}
                </Select>
                {/* There are times that we're composing the select field into another component and the helper text
                        makes the layout look weird, so we want to be able to disable it if we need to.
                    */}
                <If condition={includeHelperText}>
                    <FormHelperText error={showError}>{helperText}</FormHelperText>
                </If>
            </FormControl>
        </FieldWithAxleLabel>
    );
};
